// Ecommerce events pushed to Elevar's data layer. Every event shares one shape —
// { event, ...properties, ecommerce: { currencyCode, [key]: { actionField:
// { list }, products } } } — and every product is built from a source: a
// Storefront API variant or a Liquid-rendered `data-layer-attributes` element,
// normalized by sourceFromVariant / sourceFromDataset.

// Marks the events the theme pushes, so ElevarTransformFn can tell its own
// dl_add_to_cart apart from Elevar's. Stripped again in the transform.
const THEME_EVENT_FLAG = "theme_event";

/**
 * Where an add to cart came from, flagged on the event and on each of its
 * products.
 */
export const ADD_TO_CART_SOURCES = {
  upsell: { upsell: true },
  swatch: { swatch: true },
  stl: { stl: true },
  style_stl: { style_stl: true },
  stickyAtc: { sticky_atc: true },
};

const DEFAULT_VARIANT_TITLE = "Default Title";

const formatPrice = (amount) => (parseFloat(amount) || 0).toFixed(2);

const absoluteUrl = (url) =>
  url ? new URL(url, window.location.origin).href : undefined;

// Set in theme-scripts: the collection being browsed, else the previous page's.
const getList = () => window.theme.dataLayerList || "";

/**
 * @param {object} variant Storefront API variant, carrying its `product`
 */
export function sourceFromVariant(variant) {
  const { product } = variant;
  const variantId = window.gidToId(variant.id);

  return {
    sku: variant.sku,
    name: product.title,
    brand: product.vendor,
    category: product.productType,
    variant: variant.title,
    price: variant.price.amount,
    compareAtPrice: variant.compareAtPrice?.amount,
    productId: window.gidToId(product.id),
    variantId,
    image: variant.image?.url || product.featuredImage?.url,
    url: `${window.productUrl(product.handle)}?variant=${variantId}`,
    customColor: variant.customColor === "true",
    currentlyNotInStock: !!variant.currentlyNotInStock,
  };
}

/**
 * @param {DOMStringMap} dataset an element carrying the `data-layer-attributes`
 *        snippet
 */
export function sourceFromDataset(dataset) {
  return {
    sku: dataset.dlSku,
    name: dataset.dlName,
    brand: dataset.dlBrand,
    category: dataset.dlCategory,
    variant: dataset.dlVariant,
    price: dataset.dlPrice / 100,
    compareAtPrice: dataset.dlCompareAtPrice / 100,
    productId: dataset.dlProductId,
    variantId: dataset.dlVariantId,
    image: dataset.dlImage,
    url: dataset.dlUrl,
    customColor: dataset.dlCustomColor === "true",
    currentlyNotInStock: dataset.dlCurrentlyNotInStock === "true",
  };
}

function formatProduct(source) {
  return {
    id: source.sku || source.variantId,
    name: source.name,
    brand: source.brand,
    category: source.category,
    variant: source.variant || DEFAULT_VARIANT_TITLE,
    price: formatPrice(source.price),
    product_id: source.productId,
    variant_id: source.variantId,
  };
}

function pushEvent(event, ecommerce, properties = {}) {
  window.ElevarDataLayer?.push({
    event,
    ...properties,
    [THEME_EVENT_FLAG]: true,
    ecommerce: {
      currencyCode: window.Shopify?.currency?.active,
      ...ecommerce,
    },
  });
}

/**
 * @param {string} event
 * @param {string} key the `ecommerce` key Elevar expects for the event
 * @param {object[]} products
 * @param {object} properties custom properties for the event itself
 */
export function pushEcommerceEvent(event, key, products, properties = {}) {
  if (!products.length) return;

  const list = getList();

  pushEvent(
    event,
    {
      [key]: {
        actionField: { list },
        products: products.map((product) => ({ ...product, list })),
      },
    },
    properties,
  );
}

const stockProperties = (source) => ({
  ...(source.customColor && { custom_color: true }),
  ...(source.currentlyNotInStock && { oos: true }),
});

const detailFields = (source) => ({
  compare_at_price: formatPrice(source.compareAtPrice),
  image: absoluteUrl(source.image),
});

/**
 * @param {object} source
 */
export function trackViewItem(source) {
  pushEcommerceEvent("dl_view_item", "detail", [
    {
      ...formatProduct(source),
      ...detailFields(source),
      ...stockProperties(source),
    },
  ]);
}

/**
 * @param {object[]} impressions [{ source, position }], position 1-based
 */
export function trackViewItemList(impressions) {
  if (!impressions.length) return;

  const list = getList();

  pushEvent("dl_view_item_list", {
    impressions: impressions.map(({ source, position }) => ({
      ...formatProduct(source),
      position,
      list,
      ...detailFields(source),
    })),
  });
}

/**
 * @param {object} source
 * @param {number} position its 1-based position in the list it's shown in
 */
export function trackSelectItem(source, position) {
  pushEcommerceEvent("dl_select_item", "click", [
    { ...formatProduct(source), position },
  ]);
}

/**
 * Tracking for a cart item, carried as its `tracking` — see trackAddToCart and
 * trackRemoveFromCart.
 *
 * @param {object} source
 * @param {object} options { addon, hasMonogram }
 */
export function cartItemTracking(source, { addon, hasMonogram } = {}) {
  return {
    source,
    properties: {
      ...(addon && { addon: true }),
      ...(hasMonogram && { has_monogram: true }),
    },
  };
}

/**
 * Items without `tracking` can't be described, so are left out with a warning.
 *
 * @param {object[]} items [{ quantity, tracking }], with `tracking` as
 *        cartItemTracking builds it
 * @param {function} format extra fields for a product, from its source
 */
function cartProducts(items, format) {
  const untracked = items.filter(({ tracking }) => !tracking);
  if (untracked.length) {
    console.warn("Cart change without data layer tracking", untracked);
  }

  return items
    .filter(({ tracking }) => tracking)
    .map(({ quantity, tracking: { source, properties } }) => ({
      ...formatProduct(source),
      quantity: String(quantity),
      ...format(source),
      ...properties,
    }));
}

/**
 * @param {object[]} items [{ quantity, tracking }] — every product in the cart,
 *        in the order shown
 * @param {number} cartTotal
 */
export function trackViewCart(items, cartTotal) {
  pushEvent(
    "dl_view_cart",
    {
      actionField: { list: "Shopping Cart" },
      impressions: cartProducts(items, stockProperties).map(
        (product, index) => ({ ...product, position: index + 1 }),
      ),
    },
    { cart_total: formatPrice(cartTotal) },
  );
}

/**
 * Pushes one event per item.
 *
 * @param {object[]} items [{ quantity, tracking }] — as sent to the cart
 * @param {object} properties for each event and its product — see
 *        ADD_TO_CART_SOURCES
 */
export function trackAddToCart(items, properties = {}) {
  const products = cartProducts(items, (source) => ({
    image: absoluteUrl(source.image),
    url: absoluteUrl(source.url),
    ...stockProperties(source),
    ...properties,
  }));

  products.forEach((product) =>
    pushEcommerceEvent("dl_add_to_cart", "add", [product], properties),
  );
}

/**
 * Pushes one event per item.
 *
 * @param {object[]} items [{ quantity, tracking }] — how many of each left the
 *        cart
 */
export function trackRemoveFromCart(items) {
  cartProducts(items, (source) => ({
    image: absoluteUrl(source.image),
  })).forEach((product) =>
    pushEcommerceEvent("dl_remove_from_cart", "remove", [product]),
  );
}

export function trackGiftNoteSaved() {
  window.ElevarDataLayer?.push({ event: "gift_note_saved" });
}
